import React from "react";

/**
 * RgpdConsent — bloc de consentement RGPD obligatoire, à afficher juste avant
 * le bouton d'envoi de TOUS les formulaires du site (assurance, énergie, PER…).
 *
 * Conforme RGPD + obligations courtier ORIAS :
 *  • Texte d'information sur le traitement
 *  • Case 1 (rgpdConsent) OBLIGATOIRE — bloque le submit si non cochée
 *  • Case 2 (recontactConsent) facultative, NON pré-cochée (consentement positif)
 *  • Mention légale courtier (ORIAS 26008264, VotrAsso, ACPR, Médiation)
 *
 * L'horodatage du consentement est récupéré côté backend (au POST) pour
 * constituer la preuve de consentement exigée par la loi.
 *
 * Props :
 *  - rgpdConsent       : bool, état coché de la case 1 (obligatoire)
 *  - setRgpdConsent    : setter
 *  - recontactConsent  : bool, état coché de la case 2 (facultative)
 *  - setRecontactConsent : setter
 *  - testidPrefix      : pour cibler les data-testid (ex. "quote", "energy")
 */
export default function RgpdConsent({
  rgpdConsent,
  setRgpdConsent,
  recontactConsent,
  setRecontactConsent,
  testidPrefix = "rgpd",
}) {
  return (
    <div
      data-testid={`${testidPrefix}-consent-block`}
      className="mt-8 mb-6 border-t border-brand-border pt-6 text-[12.5px] leading-relaxed text-brand-ink/75 space-y-4"
    >
      {/* Texte d'information sur le traitement */}
      <p data-testid={`${testidPrefix}-consent-info`}>
        Les informations recueillies via ce formulaire sont enregistrées par OPTINA (Nassim Aali),
        responsable de traitement, dans le seul but de répondre à votre demande, d'établir une étude
        personnalisée et de vous recontacter à ce sujet. Elles ne sont jamais cédées à des tiers à
        des fins commerciales. Conformément au RGPD, vous disposez d'un droit d'accès, de
        rectification, d'opposition et de suppression de vos données en écrivant à{" "}
        <a
          href="mailto:nassim@optina.fr"
          className="underline hover:text-brand-ink"
        >
          nassim@optina.fr
        </a>
        . Vous pouvez retirer votre consentement à tout moment.
      </p>

      {/* Case 1 — obligatoire */}
      <label
        data-testid={`${testidPrefix}-consent-required-label`}
        className="flex items-start gap-3 cursor-pointer"
      >
        <input
          data-testid={`${testidPrefix}-consent-required`}
          type="checkbox"
          checked={!!rgpdConsent}
          onChange={(e) => setRgpdConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-brand-ink shrink-0"
          required
        />
        <span>
          J'accepte qu'OPTINA collecte et traite mes données dans le but de répondre à ma demande.{" "}
          <span className="text-brand-ink/55">(obligatoire)</span>
        </span>
      </label>

      {/* Case 2 — recontact (NON pré-cochée) */}
      <label
        data-testid={`${testidPrefix}-consent-recontact-label`}
        className="flex items-start gap-3 cursor-pointer"
      >
        <input
          data-testid={`${testidPrefix}-consent-recontact`}
          type="checkbox"
          checked={!!recontactConsent}
          onChange={(e) => setRecontactConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-brand-ink shrink-0"
        />
        <span>
          J'accepte d'être recontacté(e) par OPTINA par téléphone et/ou e-mail concernant ma demande
          de devis ou d'étude personnalisée.
        </span>
      </label>

      {/* Mention légale courtier (footer formulaire) */}
      <p
        data-testid={`${testidPrefix}-consent-courtier`}
        className="pt-3 mt-2 border-t border-brand-border/60 text-[11px] text-brand-ink/55 leading-relaxed"
      >
        OPTINA — Nassim Aali · Courtier en assurance immatriculé à l'ORIAS sous le n° 26008264 (
        <a
          href="https://www.orias.fr"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-brand-ink"
        >
          www.orias.fr
        </a>
        ) · Adhérent VotrAsso · Sous le contrôle de l'ACPR. En cas de litige :{" "}
        <a
          href="https://www.mediation-assurance.org"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-brand-ink"
        >
          La Médiation de l'Assurance
        </a>
        .
      </p>
    </div>
  );
}
